import React from 'react';
import { MagneticButton } from '../common/MagneticButton';
import { PixelGlyph } from '../common/PixelGlyph';
import { SunsetStripe } from '../common/SunsetStripe';
import { Sparkles, Zap } from 'lucide-react';

export const HeroSection: React.FC = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col justify-between pt-28 sm:pt-36 pb-8 sm:pb-12 px-4 sm:px-6 overflow-hidden z-10 bg-transparent"
      aria-label="Hero Specimen"
    >

      {/* Top Eyebrow Tag */}
      <div className="relative z-10 max-w-6xl mx-auto w-full flex flex-col items-center text-center">
        <div className="inline-flex items-center gap-2 sm:gap-2.5 px-3.5 sm:px-4 py-1.5 rounded-full bg-espresso-900/90 border border-white/10 shadow-bezel-inner text-[10px] sm:text-[11px] font-mono tracking-ultra uppercase text-cream-300 mb-6 sm:mb-8 animate-in fade-in slide-in-from-bottom-3 duration-700">
          <PixelGlyph name="pulse-dot" size={8} />
          <span>SPECIMEN NO. 01 // EXAMPLE.TH157LE.RU</span>
          <span className="text-white/20">|</span>
          <span className="text-sunset-gold font-bold">120 FPS MASTERCLASS</span>
        </div>

        {/* 2-Line Strict Editorial Heading */}
        <h1 className="font-display font-extrabold text-3xl sm:text-5xl md:text-6xl lg:text-[4.75rem] leading-[1.12] sm:leading-[1.08] tracking-tight text-cream-100 max-w-5xl mx-auto mb-6 sm:mb-8 text-balance animate-in fade-in slide-in-from-bottom-4 duration-800">
          Engineered for the <span className="bg-gradient-to-r from-cream-100 via-gold-radiance to-sunset-gold bg-clip-text text-transparent">uncompromising web</span>.
        </h1>

        {/* High-End Subtitle */}
        <p className="max-w-2xl text-sm sm:text-lg md:text-xl text-cream-muted font-normal leading-relaxed mb-8 sm:mb-10 text-balance animate-in fade-in slide-in-from-bottom-5 duration-900">
          A flagship interactive showcase combining{' '}
          <span className="text-cream-100 font-semibold underline decoration-sunset-gold/50 underline-offset-4">
            haute-couture editorial typography
          </span>{' '}
          with{' '}
          <span className="text-cream-100 font-semibold underline decoration-sunset-flame/50 underline-offset-4">
            120 FPS WebGL kinematics
          </span>
          , sub-millisecond responsiveness, and relentless attention to engineering craft.
        </p>

        {/* Dual Magnetic CTAs */}
        <div className="flex flex-col sm:flex-row items-center gap-3.5 sm:gap-5 mb-12 sm:mb-16 w-full sm:w-auto animate-in fade-in slide-in-from-bottom-6 duration-1000">
          <MagneticButton
            variant="sunset"
            size="lg"
            href="#specimens"
            showArrow={true}
            className="w-full sm:w-auto justify-center"
          >
            Explore Showcase
          </MagneticButton>

          <MagneticButton
            variant="secondary"
            size="lg"
            href="#playground"
            showArrow={true}
            className="w-full sm:w-auto justify-center"
          >
            Live Performance Demo
          </MagneticButton>
        </div>
      </div>

      {/* Bottom Live Hardware Ticker */}
      <div className="relative z-10 max-w-6xl mx-auto w-full">
        <div className="rounded-2xl bg-[#0e0e13]/90 border border-white/10 p-3 sm:p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 text-xs font-mono text-cream-muted">
          <div className="flex items-center gap-2.5">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-cream-100 font-semibold text-[11px] sm:text-xs">STATUS: ACCELERATED CLUSTER ONLINE</span>
          </div>

          <div className="flex items-center justify-between w-full sm:w-auto gap-4 sm:gap-6 text-[11px] sm:text-xs">
            <div className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-sunset-gold" />
              <span className="text-cream-dim">ENGINE:</span>
              <span className="text-sunset-gold font-bold">120 FPS</span>
            </div>

            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-sunset-flame" />
              <span className="text-cream-dim">LATENCY:</span>
              <span className="text-cream-100 font-bold">0.4 MS</span>
            </div>

            <div className="hidden md:flex items-center gap-1.5">
              <span className="text-cream-dim">HOST:</span>
              <span className="text-gold-champagne font-bold">example.th157le.ru</span>
            </div>
          </div>
        </div>

        <SunsetStripe className="mt-3 sm:mt-4 opacity-80" height="h-1" />
      </div>
    </section>
  );
};
